import React, { useState, useEffect, useRef } from "react";
import { cn } from "tailwind-variants";
import { useI18n } from "intl/useI18n";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { DTF_PROGRESS_MAPPING } from "common/utils/progress-bar/constants";
import { useDtfStore } from "./store";
import "./DtfProgressBar.scss";

const UPDATE_INTERVAL = 50.0;

export default function DtfProgressBar({
  artwork = null,
  index = null,
  onClickRemove,
  ...restProps
}) {
  const { t } = useI18n();
  const dtfStore = useDtfStore();

  // State
  const [previousTimeStamp, setPreviousTimeStamp] = useState(0);
  const [isRunning, setIsRunning] = useState(true);
  const [currentProgress, setCurrentProgress] = useState(artwork?.currentProgress || 0);
  const [currentProgressPercentage, setCurrentProgressPercentage] = useState("");
  const [progressText, setProgressTextState] = useState("");

  // Refs
  const timeoutHandleRef = useRef(null);
  const currentProgressRef = useRef(currentProgress);

  // Update refs when state changes
  useEffect(() => {
    currentProgressRef.current = currentProgress;
  }, [currentProgress]);

  // Computed properties (as functions)
  function isRetrying() {
    return dtfStore.isRetrying;
  }

  function showModal() {
    return dtfStore.showModal;
  }

  function actionCableConnected() {
    return dtfStore.actionCableConnected;
  }

  function isSpangle() {
    return dtfStore.isSpangle;
  }

  function isStandardArtwork() {
    return dtfStore.isStandardArtwork;
  }

  function artworks() {
    return dtfStore.artworks;
  }

  function progressError() {
    return artwork?.uploadError;
  }

  function progress() {
    return artwork?.progress;
  }

  function progressMapIndex() {
    return isSpangle() ? 1 : 0;
  }

  function fileSize() {
    return Math.floor((artwork?.file?.size || 0) * 0.000001 || 0);
  }

  function remainingTime() {
    const spangleTime = isSpangle() ? 1 : 0;
    if (Math.ceil(fileSize() / 10) < 1) {
      return 1 + spangleTime;
    }
    return Math.min(Math.ceil(fileSize() / 10), 15) + spangleTime;
  }

  function progressState() {
    if (progressError()) return "error";
    if (navigator.onLine && actionCableConnected()) return "online";
    return "offline";
  }

  function isOnline() {
    return progressState() === "online" && !artwork?.retryFailed && actionCableConnected();
  }

  function showErrorState() {
    return progressError() || artwork?.retryFailed;
  }

  function showProcessing() {
    return isOnline();
  }

  function artworkPresentInStore() {
    return artworks().findIndex(art => art.id === artwork?.id) > -1;
  }

  // Methods
  function removeArtworkByIndex(...args) {
    return dtfStore.removeArtworkByIndex(...args);
  }

  function setIsRetrying(...args) {
    return dtfStore.setIsRetrying(...args);
  }

  function updateArtworkValues(...args) {
    return dtfStore.updateArtworkValues(...args);
  }

  function updateArtworkValuesById(...args) {
    return dtfStore.updateArtworkValuesById(...args);
  }

  function setProgressPercentage() {
    const percentage = progressError() ? "100%" : `${currentProgressRef.current.toFixed(2)}%`;
    setCurrentProgressPercentage(percentage);
  }

  function setProgressText() {
    setProgressTextState(currentProgressPercentage);
  }

  function deleteArtwork() {
    if (index === null) {
      onClickRemove?.();
    } else {
      removeArtworkByIndex(index);
    }
  }

  function handleOnlineStatusChange() {
    const online = navigator.onLine;
    if (!isOnline()) {
      setIsRunning(false);
      return;
    }
    if (online) {
      setIsRunning(true);
      setIsRetrying(false);
    }
  }

  function cleanupAndSaveState() {
    if (timeoutHandleRef.current) {
      clearTimeout(timeoutHandleRef.current);
    }
    if (!artworkPresentInStore()) {
      updateArtworkValues({
        index,
        currentProgress: currentProgressRef.current
      });
    }
  }

  function incrementProgress() {
    const cp = currentProgressRef.current;
    const prog = progress();

    if (cp >= prog && artworkPresentInStore()) {
      updateArtworkValues({
        index: artwork?.originalIndex,
        currentProgress: cp
      });
      return;
    }

    const keys = Object.keys(DTF_PROGRESS_MAPPING);
    const currentStageKey = keys.find(
      key => DTF_PROGRESS_MAPPING[key].percentage[progressMapIndex()] > cp
    );
    const currentStage = DTF_PROGRESS_MAPPING[currentStageKey];
    let counterInterval = currentStage.interval[progressMapIndex()] || currentStage.interval[0];
    const { rate } = currentStage;
    const nextStageKey = keys.find(
      key => DTF_PROGRESS_MAPPING[key].percentage[progressMapIndex()] === prog
    );
    const nextStage = DTF_PROGRESS_MAPPING[nextStageKey];
    const newProgress = parseFloat((cp + rate).toFixed(2));
    setCurrentProgress(newProgress);
    const speedUp = keys.indexOf(nextStageKey) > keys.indexOf(currentStageKey);
    if (speedUp) {
      counterInterval = currentStage.catchUpInterval;
      if (nextStage.skipCatchUp) {
        setCurrentProgress(nextStage.percentage[progressMapIndex()]);
      }
    }
    setProgressPercentage();
    setProgressText();
    timeoutHandleRef.current = setTimeout(incrementProgress, counterInterval);
  }

  // Lifecycle and event listeners
  useEffect(() => {
    setCurrentProgress(artwork?.currentProgress || 0);
    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);
    incrementProgress();

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
      cleanupAndSaveState();
    };
  }, []);

  // Watch progress
  useEffect(() => {
    incrementProgress();
  }, [artwork?.progress]);

  // Watch progressState
  useEffect(() => {
    setProgressText();
  }, [artwork?.uploadError, dtfStore.actionCableConnected]);

  // Watch isOnline
  useEffect(() => {
    setProgressText();
  }, [artwork?.retryFailed, dtfStore.actionCableConnected]);

  return (
    <div
      {...restProps}
      className={cn("dtf-progress-bar scope-eec1bfa0", restProps?.className)}
      style={{
        "--eec1bfa0-currentProgressPercentage": currentProgressPercentage
      }}
    >
      {!showErrorState() ? (
        <div
          className={cn("scope-eec1bfa0", {
            "dtf-progress-bar__line-wrap": !isStandardArtwork(),
            "dtf-progress-bar__line-wrap-ab-comparison": isStandardArtwork()
          })}
        >
          {!isStandardArtwork() ? (
            <div
              className="dtf-progress-bar__line scope-eec1bfa0"
              style={{
                width: currentProgressPercentage
              }}
            />
          ) : null}
          {isStandardArtwork() ? (
            <div className="dtf-progress-bar__line-ab-comparison scope-eec1bfa0" />
          ) : null}
        </div>
      ) : null}
      <div
        className={cn("scope-eec1bfa0", {
          "dtf-progress-bar__wrapper": !isStandardArtwork(),
          "dtf-progress-bar__wrapper-ab-comparison": isStandardArtwork()
        })}
      >
        {showErrorState() && showModal() ? (
          <button
            className="dtf-progress-bar__remove-button scope-eec1bfa0"
            type="button"
            onClick={deleteArtwork}
            data-testid="dtf-progress-bar-remove-button"
          >
            <UseSvg
              className="dtf-progress-bar__trash-icon scope-eec1bfa0"
              icon="trash"
              data-testid="dtf-progress-bar-trash-icon"
            />{" "}
            Remove{" "}
          </button>
        ) : null}
        {!showErrorState() ? (
          <div className="dtf-progress-bar__value scope-eec1bfa0">
            {progressText}{" "}
            <span className="dtf-progress-bar__remaining-time scope-eec1bfa0">
              {" "}
              (Less than {t("minute", remainingTime())} left){" "}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
